import React from 'react';
import { MusicTheory } from '../music-theory';

interface NoteButtonsProps {
    currentKey: string;
    onNoteSelect: (note: string) => void;
    disabled?: boolean;
}

const NoteButtons: React.FC<NoteButtonsProps> = ({ currentKey, onNoteSelect, disabled = false }) => {
    // Get the chromatic notes for this key from MusicTheory
    const notes = MusicTheory.getChromaticNotesForKey(currentKey);

    // Get button styling based on whether it's an accidental
    const getButtonStyle = (note: string) => {
        const baseStyle = `
            flex-1 min-w-0 py-4 sm:py-3 px-2 rounded-lg sm:rounded-xl font-medium text-base sm:text-sm md:text-base
            transition-all duration-200 active:scale-95 shadow-md hover:shadow-lg
            disabled:opacity-50 disabled:cursor-not-allowed
            min-h-[48px] touch-manipulation
        `;

        if (MusicTheory.isAccidental(note)) {
            // Accidental notes (sharps/flats) - darker styling
            return `${baseStyle} bg-slate-800 hover:bg-slate-700 text-white border-2 border-slate-600`;
        } else {
            // Natural notes - lighter styling
            return `${baseStyle} bg-white hover:bg-slate-50 text-slate-800 border-2 border-slate-300`;
        }
    };

    return (
        <div className="bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4 shadow-lg border border-white/20">
            {/* Simplified header - remove redundant info on mobile */}
            <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-3 text-center sm:block hidden">
                Select the note ({currentKey} major scale)
            </h3>
            
            {/* Mobile-only compact header */}
            <h3 className="text-sm font-medium text-slate-700 mb-3 text-center sm:hidden">
                {currentKey} major
            </h3>

            {/* Note buttons grid - optimized for mobile touch */}
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2 sm:gap-3">
                {notes.map((note) => (
                    <button
                        key={note}
                        onClick={() => onNoteSelect(note)}
                        disabled={disabled}
                        className={getButtonStyle(note)}
                        aria-label={`Note ${note.replace('#', ' sharp').replace('b', ' flat')}`}
                    >
                        {note}
                    </button>
                ))}
            </div>

            {/* Helper text - hide on mobile to save space */}
            <p className="text-xs sm:text-sm text-slate-500 text-center mt-3 hidden sm:block">
                {MusicTheory.getKeySignatureDescription(currentKey)}
            </p>
        </div>
    );
};

export default NoteButtons;